import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NavigateFunction } from 'react-router-dom';

import { getUrlParamsFetch, notFoundIdHandler } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import { TipoAvisoMedio, TipoAvisoMedioPagingRes } from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';

const { get, post, put } = erpApi();

///* tanSctack query
export const useFetchTipoAvisoMedios = (params?: GetTipoAvisoMediosParams) => {
  return useQuery({
    queryKey: ['tipoAvisoMedios', ...Object.values(params || {})],
    queryFn: async () => {
      return getTipoAvisoMedios(params);
    },
  });
};

export const useGetTipoAvisoMedio = (id: string) => {
  return useQuery({
    queryKey: ['tipoAvisoMedio', id],
    queryFn: async () => {
      return getTipoAvisoMedio(id);
    },
  });
};

export const useCreateTipoAvisoMedio = (
  navigate: NavigateFunction,
  returnUrl: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createTipoAvisoMedio,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tipoAvisoMedios'] });
    },
    onSettled: () => {
      navigate(returnUrl);
    },
  });
};

export const useUpdateTipoAvisoMedio = (
  navigate: NavigateFunction,
  returnUrl: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateTipoAvisoMedio,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tipoAvisoMedios'] });
    },
    onSettled: () => {
      navigate(returnUrl);
    },
  });
};

///* axios
export type GetTipoAvisoMediosParams = {
  page?: number;
  page_size?: number;
  nombre?: string;
  codigo?: string;
};

export type CreateTipoAvisoMedioParams = Omit<
  TipoAvisoMedio,
  'id_tipo_aviso_medio'
>;
export type UpdateTipoAvisoMedioParams = {
  id: string;
  data: CreateTipoAvisoMedioParams;
};

export const getTipoAvisoMedios = async (params?: GetTipoAvisoMediosParams) => {
  const queryParams = getUrlParamsFetch(params || {});

  return get<TipoAvisoMedioPagingRes>(
    `${urlBackend}api/v1/tipos-aviso-medios/?${queryParams}`,
    true
  );
};

export const getTipoAvisoMedio = async (id: string) => {
  const res = await get<TipoAvisoMedio>(
    `${urlBackend}api/v1/tipos-aviso-medios/${id}/`,
    true
  );

  notFoundIdHandler(res);
  return res;
};

export const createTipoAvisoMedio = async (
  data: CreateTipoAvisoMedioParams
) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await post<TipoAvisoMedio>(
    `${urlBackend}api/v1/tipos-aviso-medios/`,
    data,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};

export const updateTipoAvisoMedio = async ({
  id,
  data,
}: UpdateTipoAvisoMedioParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await put<TipoAvisoMedio>(
    `${urlBackend}api/v1/tipos-aviso-medios/${id}/`,
    data,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};
